import React, { useEffect, useRef, useState } from 'react';
import { Mic, TrendingUp, Check } from 'lucide-react';

/**
 * The hero's product visual: a TPI score card that draws itself in.
 *
 * Built entirely from SVG/CSS rather than a screenshot so it stays crisp at any
 * size, needs no asset pipeline, and animates. It shows the two things that
 * matter about the product in one glance - a single headline score, and the
 * per-dimension breakdown behind it.
 */

const SCORE = 945;
const MAX = 1200;
const RADIUS = 88;
const CIRC = 2 * Math.PI * RADIUS;

const DIMENSIONS = [
  { label: 'Fluency', value: 92 },
  { label: 'Pronunciation', value: 86 },
  { label: 'Grammar', value: 95 },
  { label: 'Vocabulary', value: 81 }
];

const ScoreRingCard = () => {
  const [shown, setShown] = useState(false);
  const [count, setCount] = useState(0);
  const hostRef = useRef(null);

  // Draw once the card is on screen (and immediately if motion is reduced).
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      setCount(SCORE);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShown(true);
        io.disconnect();

        // Count the number up so the score feels "computed", not printed.
        const DURATION = 1500;
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / DURATION, 1);
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          setCount(Math.round(SCORE * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const offset = CIRC - (SCORE / MAX) * CIRC;

  return (
    <div ref={hostRef} className="relative mx-auto w-full max-w-[440px]">
      {/* Soft glow behind the card so it lifts off the page. */}
      <div
        aria-hidden="true"
        className="absolute -inset-8 -z-10 rounded-[40px] opacity-70 blur-3xl"
        style={{
          background:
            'radial-gradient(60% 55% at 50% 40%, rgba(52,89,164,0.22), transparent 70%)'
        }}
      />

      <div className="glass-panel p-7">
        {/* Card header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
              Tenses Proficiency Index
            </p>
            <p className="mt-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Certified result
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold"
            style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
          >
            <Check className="h-3 w-3" />
            Verified
          </span>
        </div>

        {/* Score ring */}
        <div className="relative mx-auto grid h-[212px] w-[212px] place-items-center">
          <svg viewBox="0 0 212 212" className="absolute inset-0 h-full w-full -rotate-90">
            <defs>
              <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4c80c2" />
                <stop offset="60%" stopColor="#3459a4" />
                <stop offset="100%" stopColor="#8FEC78" />
              </linearGradient>
            </defs>
            <circle className="ring-track" cx="106" cy="106" r={RADIUS} fill="none" strokeWidth="13" />
            <circle
              className="ring-value"
              cx="106"
              cy="106"
              r={RADIUS}
              fill="none"
              strokeWidth="13"
              stroke="url(#ringGrad)"
              strokeDasharray={CIRC}
              strokeDashoffset={shown ? offset : CIRC}
            />
          </svg>

          <div className="text-center">
            <div
              className="display tabular-nums"
              style={{ fontSize: '3.15rem', lineHeight: 1, color: 'var(--text-primary)' }}
            >
              {count}
            </div>
            <div className="mt-1 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
              of {MAX}
            </div>
            <div
              className="mt-2.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.72rem] font-semibold"
              style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
            >
              Expert · C1
            </div>
          </div>
        </div>

        {/* Dimension bars */}
        <div className="mt-7 space-y-3.5">
          {DIMENSIONS.map((d, i) => (
            <div key={d.label}>
              <div className="mb-1.5 flex items-center justify-between text-[0.78rem]">
                <span style={{ color: 'var(--text-body)' }}>{d.label}</span>
                <span className="tabular-nums font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {d.value}%
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: shown ? `${d.value}%` : '0%',
                    transitionDelay: `${350 + i * 120}ms`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating chips: hint at the live scoring without cluttering the card. */}
      <div
        className="animate-float glass-panel absolute -left-6 top-[26%] hidden items-center gap-2.5 rounded-2xl px-3.5 py-2.5 sm:flex"
        style={{ animationDelay: '0.8s' }}
      >
        <span
          className="grid h-8 w-8 place-items-center rounded-xl"
          style={{ background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}
        >
          <Mic className="h-4 w-4" />
        </span>
        <div className="leading-tight">
          <p className="text-[0.72rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
            Speech scored
          </p>
          <p className="text-[0.68rem]" style={{ color: 'var(--text-muted)' }}>
            in seconds
          </p>
        </div>
      </div>

      <div
        className="animate-float glass-panel absolute -right-4 bottom-[16%] hidden items-center gap-2.5 rounded-2xl px-3.5 py-2.5 sm:flex"
        style={{ animationDelay: '2.4s' }}
      >
        <span
          className="grid h-8 w-8 place-items-center rounded-xl"
          style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
        >
          <TrendingUp className="h-4 w-4" />
        </span>
        <div className="leading-tight">
          <p className="text-[0.72rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
            +370 points
          </p>
          <p className="text-[0.68rem]" style={{ color: 'var(--text-muted)' }}>
            in 3 months
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScoreRingCard;
